import type { Option } from "../constants";
import { formatScreenFieldDisplayLabel } from "./resource-insert-label-utils";

/**
 * Human-readable assignment RHS — same ordering as the Value `FormEntitySelect` in `AssignmentForm`.
 * Supports form rows and persisted node data (`valueScreenTableField` / `valueDatabaseTableField` from save/API).
 */
export type AssignmentValueLabelSource = {
	value?: unknown;
	plainTextValue?: string;
	valueLiteral?: string;
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueAdvancedTriggerId?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueDatabaseSingleRowVariableId?: string;
	valueScreenFieldName?: string;
	valueAdvancedTriggerName?: string;
	valueDatabaseTableFieldName?: string;
	valueScreenTableField?: { name?: string };
	valueScreenField?: { name?: string };
	valueDatabaseTableField?: { name?: string };
	valueSimpleVariableName?: string;
	valueFormulaVariableName?: string;
	valueDatabaseSingleRowVariableName?: string;
	valueGlobalConstant?: string;
	valueGlobalConstantName?: string;
	valueResourceCollectionId?: string;
	valueCollectionName?: string;
};

export function resolveGlobalConstantLabel(
	id: string | undefined,
	options?: Option[],
): string | undefined {
	if (!id?.trim() || !options?.length) return undefined;
	return options.find((o) => o.value === id)?.label;
}

/** Canonical id/text bound to `FormEntitySelect` `value` (not display label). */
export function getAssignmentValueFormEntityBinding(
	statement: AssignmentValueLabelSource,
): string {
	if (statement.valueGlobalConstant) return statement.valueGlobalConstant;
	if (statement.valueResourceCollectionId) return statement.valueResourceCollectionId;
	if (statement.valueDatabaseTableFieldId) return statement.valueDatabaseTableFieldId;
	if (statement.valueScreenFieldName) return statement.valueScreenFieldName;
	if (statement.valueAdvancedTriggerId) return statement.valueAdvancedTriggerId;
	if (statement.valueDatabaseSingleRowVariableId) {
		return statement.valueDatabaseSingleRowVariableId;
	}
	if (statement.valueSimpleVariableName) return statement.valueSimpleVariableName;
	if (statement.valueFormulaVariableName) return statement.valueFormulaVariableName;
	if (typeof statement.plainTextValue === "string") return statement.plainTextValue;
	if (typeof statement.valueLiteral === "string") return statement.valueLiteral;
	if (typeof statement.value === "string") return statement.value;
	return "";
}

function resolveScreenFieldDisplayName(
	name: string | undefined,
	fieldId: string | undefined,
	currentScreenFieldIds?: ReadonlySet<string>,
): string | undefined {
	if (typeof name !== "string" || !name.trim()) return undefined;
	const trimmed = name.trim();
	if (!currentScreenFieldIds) return trimmed;
	return formatScreenFieldDisplayLabel(trimmed, fieldId, currentScreenFieldIds);
}

export function getAssignmentValueDisplayLabel(
	statement: AssignmentValueLabelSource,
	globalConstantOptions?: Option[],
	currentScreenFieldIds?: ReadonlySet<string>,
): string {
	const fromEntity =
		resolveScreenFieldDisplayName(
			statement.valueScreenFieldName,
			statement.valueScreenFieldId,
			currentScreenFieldIds,
		) ??
		statement.valueDatabaseTableFieldName ??
		statement.valueAdvancedTriggerName ??
		resolveScreenFieldDisplayName(
			statement.valueScreenTableField?.name,
			statement.valueScreenFieldId,
			currentScreenFieldIds,
		) ??
		resolveScreenFieldDisplayName(
			statement.valueScreenField?.name,
			statement.valueScreenFieldId,
			currentScreenFieldIds,
		) ??
		statement.valueDatabaseTableField?.name ??
		statement.valueSimpleVariableName ??
		statement.valueFormulaVariableName ??
		statement.valueDatabaseSingleRowVariableName ??
		statement.valueGlobalConstantName ??
		resolveGlobalConstantLabel(statement.valueGlobalConstant, globalConstantOptions);

	if (typeof fromEntity === "string" && fromEntity.trim()) {
		return fromEntity.trim();
	}

	const fromPlain =
		(typeof statement.plainTextValue === "string" && statement.plainTextValue.trim()) ||
		(typeof statement.valueLiteral === "string" && statement.valueLiteral.trim()) ||
		"";

	if (fromPlain) return fromPlain;

	if (typeof statement.value === "string" && statement.value.trim()) {
		return statement.value.trim();
	}

	return "";
}
